import endpoints from 'constants/endpoints';
import { TRANSFER_TYPES } from 'constants/enum';
import { refreshUser } from './actions';
import { showAlert, showAlertModal } from './site';

import {
  formatDateToMMDDYYYY,
  getActionablePendingAppointments,
  getCarriers,
  getParentCarrierName,
  getRequestedAppointmentsByCarrier,
  getTodaysDateAsInt
} from 'helpers/appointments/appointments';

import {
  safeAWSCallGet,
  safeAWSCallPost,
  safePostCall
} from 'helpers/firebaseSetup';
import {
  parseZohoJsonOutput,
  parseZohoOkMessageOutput
} from 'helpers/zoho/response-parsers';

import {
  GET_APPOINTED_CARRIERS,
  GET_CARRIERS,
  SET_CARRIERS_ARRAY,
  SET_STATEMENTS_CARRIERS_ARRAY,
  GET_REQUESTED_APPOINTMENTS,
  IS_SENDING_REQUEST_APPOINTMENTS,
  IS_UPDATING_REQUEST_APPOINTMENTS,
  GET_STATEMENT_PERIODS,
  SET_REQUESTED_CARRIER_NAME,
  SET_IS_NEO_SUBMISSION_MODAL_OPEN,
  UPDATE_LOADING_APPOINTMENTS_DATA,
  SET_ATTACHMENTS,
  SET_FAQS_BY_CARRIER
} from 'constants/types';
import { sanitizeApptRequests } from 'helpers/factory/appointmentFactory';
import {
  carrierFactory,
  carriersObjectFactory
} from 'helpers/factory/carrierFactory';
import { getCompany } from 'helpers/getCompanyVariables';

// action creators

const company = getCompany();

/**
 * sets the currently requested carrier name in redux state
 * @param payload - carrier name to store
 */
export const setRequestedCarrierName = payload => ({
  type: SET_REQUESTED_CARRIER_NAME,
  payload
});

/**
 * opens or closes the pop-up that sends the agent to Neo's form after a Neo request
 * @param payload - whether the pop-up is open
 */
export const setIsNeoSubmissionModalOpen = payload => ({
  type: SET_IS_NEO_SUBMISSION_MODAL_OPEN,
  payload
});

const setIsSendingRequestappointments = payload => {
  return {
    type: IS_SENDING_REQUEST_APPOINTMENTS,
    payload
  };
};
const setIsUpdatingRequestAppointments = payload => {
  return {
    type: IS_UPDATING_REQUEST_APPOINTMENTS,
    payload
  };
};

/**
 * fetches carriers, attachments and requested appointments for the current agent and dispatches them to redux
 * @param setLocalLoader - toggles the local loading state
 * @param cancelTokenSource - axios cancel token source for the request
 * @param globalload - whether to use the global loader instead of the local one
 * @param onSuccess - callback invoked once the data has been processed
 */
export const getAppointmentsViewData =
  (
    setLocalLoader = () => {},
    cancelTokenSource = {},
    globalload = false,
    onSuccess = () => {}
  ) =>
  async (dispatch, getState) => {
    // only if the page is refreshed
    // if (!currentCarriers) {
    if (globalload === true) {
      setLocalLoader(true);
    } else {
      dispatch({
        type: UPDATE_LOADING_APPOINTMENTS_DATA,
        payload: true
      });
      setLocalLoader(false);
    }
    const zohoId = getState().user.zohoData.id;
    const agencyId = getState().user.zohoData.Account_Name?.id || null;

    // refreshes the user that's been pulled from zoho in case something changed
    await dispatch(refreshUser());

    const finalize = () => {
      onSuccess?.();
      setLocalLoader(false);

      dispatch({
        type: UPDATE_LOADING_APPOINTMENTS_DATA,
        payload: false
      });
    };

    safePostCall(
      endpoints.awsProxy.safePost,
      {
        zohoId,
        agencyId,
        endpoint: endpoints.zoho.getCarriersAndAppointments,
        isZohoRequest: true
      },
      { cancelToken: cancelTokenSource.token }
    )
      .then(carriersRes => {
        if (!carriersRes) {
          finalize();
          return;
        }

        const { ok, message, data } = parseZohoJsonOutput(
          carriersRes.data.details.output
        );

        if (!ok) {
          dispatch(showAlert({ message, code: 'error' }));
          finalize();
          return;
        }

        const zohoData = getState().user.zohoData;
        // gets the carriers and formats them
        let carriers = [...(data.carriers || [])];
        // gets a copy of the carriers array exclusive for the statements tab
        let statementsCarriers = [...(data.carriers || [])];
        // gets a copy of the attachments array
        const attachments = [...(data.attachments || [])];
        // filter depending on the form preferences
        if (zohoData.agencyData.Hide_Medicare === true) {
          carriers = carriers.filter(
            carrier => carrier.Appointment_Type !== 'Medicare'
          );
        }
        if (zohoData.agencyData.Hide_Life === true) {
          carriers = carriers.filter(
            carrier => carrier.Appointment_Type !== 'Life'
          );
        }
        // create factory of carriers and filter depending on the preferences
        carriers = carriers
          .map(carrier => carrierFactory(carrier, zohoData))
          .sort((a, b) => a.name.localeCompare(b.name))
          .filter(({ exclude }) => !exclude);
        statementsCarriers = statementsCarriers
          .map(carrier => carrierFactory(carrier, zohoData))
          .sort((a, b) => a.name.localeCompare(b.name));

        // if we haven't added the faqs by carrier yet, add them
        if (getState().faqs.faqsByCarrier === null) {
          const faqsByCarrier = {};
          // set as undefined to indicate that the faqs for the carrier are not yet loaded
          carriers.forEach(carrier => {
            faqsByCarrier[carrier.name] = undefined;
          });
          dispatch({
            type: SET_FAQS_BY_CARRIER,
            payload: faqsByCarrier
          });
        } else {
          // if we have already added the faqs by carrier, add carriers in case that the carrier is not in the list
          const faqsByCarrier = getState().faqs.faqsByCarrier;
          carriers.forEach(carrier => {
            if (!faqsByCarrier[carrier.name]) {
              faqsByCarrier[carrier.name] = undefined;
            }
          });
          dispatch({
            type: SET_FAQS_BY_CARRIER,
            payload: faqsByCarrier
          });
        }
        // builds the carriersByType for redux
        const carriersByType = carriersObjectFactory(carriers);
        // sends payload to set carriers redux state

        // sends payload to set requested appointments redux state
        const requestedAppointments = sanitizeApptRequests(
          carriersByType,
          data.appointment_requests || [],
          zohoData
        );

        const actionableAppointments =
          company === 'oneill'
            ? getActionablePendingAppointments(requestedAppointments)
            : [];

        const requestedAppointmentsByCarrier =
          getRequestedAppointmentsByCarrier(carriers, requestedAppointments);

        dispatch({
          type: GET_CARRIERS,
          payload: getCarriers(carriersByType, requestedAppointments, zohoData)
        });
        dispatch({ type: SET_CARRIERS_ARRAY, payload: carriers });
        dispatch({ type: SET_ATTACHMENTS, payload: attachments || [] });
        dispatch({
          type: SET_STATEMENTS_CARRIERS_ARRAY,
          payload: statementsCarriers
        });
        dispatch({
          type: GET_APPOINTED_CARRIERS,
          payload: getCarriers(
            carriersByType,
            requestedAppointments,
            zohoData,
            [
              'confirmed' // we only need the confirmed carriers
            ]
          )
        });
        dispatch({
          type: GET_REQUESTED_APPOINTMENTS,
          payload: {
            requestedAppointments,
            actionableAppointments,
            requestedAppointmentsByCarrier
          }
        });

        finalize();
      })
      .catch(error => {
        console.error(error);
        if (error?.message !== 'API Call Canceled') setLocalLoader(false);
      });
  };

/**
 * fetches the available commission statement periods
 * @param setLocalLoader - toggles the local loading state
 */
export const getStatementsPeriods =
  (setLocalLoader = () => {}) =>
  async dispatch => {
    safeAWSCallGet(endpoints.aws.comissionsPeriods, {}, false)
      .then(periodsRes => {
        if (periodsRes) {
          dispatch({
            type: GET_STATEMENT_PERIODS,
            payload: periodsRes.data.map(p => ({ name: p, value: p }))
          });
        }
        setLocalLoader(false);
      })
      .catch(error => {
        console.error(error);
        if (error?.message !== 'API Call Canceled') setLocalLoader(false);
      });
  };

function getAlreadyUploadedValidFiles(
  attachments,
  requestedStates,
  requiredActions,
  carrierName = ''
) {
  const fileUploadActions = requiredActions.filter(
    ({ type }) => type === 'fileUpload'
  );

  if (fileUploadActions.length === 0) {
    return [];
  }

  const currentDate = getTodaysDateAsInt();

  const filteredAttachmentNames = new Set();

  fileUploadActions.forEach(action => {
    const { name, state, expires, carrierBased } = action;

    // if the state is not in the requested states, skip it
    if (state && !requestedStates.includes(state)) {
      return;
    }

    const formattedStateName = state ? state.replace(/\s/g, '_') : null;
    const formattedCarrierName = carrierName.replace(/\s/g, '_');

    // create the regex pattern
    let filePattern;
    if (carrierBased) {
      if (expires && formattedStateName) {
        filePattern = new RegExp(
          `^${formattedCarrierName}-${name}-\\d{8}-${formattedStateName}`
        );
      } else if (expires && !formattedStateName) {
        filePattern = new RegExp(`^${formattedCarrierName}-${name}-\\d{8}`);
      } else if (!expires && formattedStateName) {
        filePattern = new RegExp(
          `^${formattedCarrierName}-${name}-${formattedStateName}`
        );
      } else {
        filePattern = new RegExp(`^${formattedCarrierName}-${name}`);
      }
    } else {
      if (expires && formattedStateName) {
        filePattern = new RegExp(`^${name}-\\d{8}-${formattedStateName}`);
      } else if (expires && !formattedStateName) {
        filePattern = new RegExp(`^${name}-\\d{8}`);
      } else if (!expires && formattedStateName) {
        filePattern = new RegExp(`^${name}-${formattedStateName}`);
      } else {
        filePattern = new RegExp(`^${name}`);
      }
    }

    // with the pattern, filter the attachments
    attachments.forEach(attachment => {
      if (!filePattern.test(attachment.name)) {
        return;
      }

      // no need to check the date if it doesn't expire but file is valid
      if (!expires) {
        filteredAttachmentNames.add(attachment.name);
        return;
      }

      // check the date
      const dateMatch = attachment.name.match(/-(\d{8})(?:-|$)/);
      if (dateMatch && dateMatch[1]) {
        const fileDate = parseInt(dateMatch[1]);
        // check if the file date is greater than or equal to the current date
        if (fileDate >= currentDate) {
          filteredAttachmentNames.add(attachment.name);
        }
      }
    });
  });

  return [...filteredAttachmentNames];
}

function getAcknowledgements(requiredActions, formData) {
  return (requiredActions || [])
    .filter(
      action =>
        action?.type?.toLowerCase()?.includes('acknowledgement') &&
        formData[action.type]
    )
    .map(({ description, state }) => ({ description, state }));
}

async function sendRequestedAppointments(
  user,
  data,
  formData,
  cancelTokenSource,
  dispatch,
  onSuccess = () => {}
) {
  try {
    const response = await safePostCall(endpoints.awsProxy.safePost, formData, {
      cancelToken: cancelTokenSource.token
    });

    const { ok, message } = parseZohoOkMessageOutput(
      response?.data?.details?.output
    );

    if (!ok) {
      dispatch(
        showAlert({
          message:
            message ||
            'An error occurred while creating your appointment request',
          code: 'error'
        })
      );

      return false;
    }

    await dispatch(getAppointmentsViewData());
    const title = 'Request sent successfully! Appointment instructions:';

    if (message) {
      dispatch(showAlert({ message, code: 'success' }));
    }

    onSuccess && onSuccess();

    if (data.carrierData.primaryCarrier && user.Oneill_Email) {
      dispatch(
        showAlertModal({
          title,
          message:
            'Appointment information will be sent to your O’Neill Insurance email account. Our team will work to complete the contracting for you.',
          code: 'success'
        })
      );
    } else {
      if (
        data.transfer === TRANSFER_TYPES.DirectWithCarrier &&
        data.carrierData.customNoteTransferDirectwithCarrier
      ) {
        dispatch(
          showAlertModal({
            title,
            message: data.carrierData.customNoteTransferDirectwithCarrier
          })
        );
      } else if (
        data.transfer === TRANSFER_TYPES.ThroughAnotherAgency &&
        data.carrierData.customNoteTransferThroughAnotherAgency
      ) {
        dispatch(
          showAlertModal({
            title,
            message: data.carrierData.customNoteTransferThroughAnotherAgency
          })
        );
      } else if (
        data.transfer === TRANSFER_TYPES.NewAppointment &&
        data.carrierData.customNoteNewAppointment
      ) {
        dispatch(
          showAlertModal({
            title,
            message: data.carrierData.customNoteNewAppointment
          })
        );
      }
    }

    return true;
  } catch (error) {
    console.error(error);
    dispatch(
      showAlert({
        message: 'Something went wrong. Try again later.',
        code: 'error'
      })
    );

    return false;
  } finally {
    dispatch(setIsSendingRequestappointments(false));
  }
}

/**
 * sends a new appointment request for the agent, uploading any required files first
 * @param cancelTokenSource - axios cancel token source for the request(s)
 * @param data - appointment request form data
 * @param onSuccess - callback invoked once the request has been sent
 */
export const createAppointmentRequest =
  (cancelTokenSource = {}, data, onSuccess = () => {}) =>
  async (dispatch, getState) => {
    return new Promise(async resolve => {
      try {
        dispatch(setIsSendingRequestappointments(true));
        const user = getState().user.zohoData;

        const formData = {
          agentId: user.id,
          states: data.states,
          transfer: data.transfer,
          carrierName: getParentCarrierName(data.carrierName),
          endpoint: endpoints.zoho.createAppointmentRequest,
          isZohoRequest: true
        };

        dispatch(
          showAlert({
            message: 'Sending request',
            code: 'info'
          })
        );

        const { files, requiredActions, expirationDates, attachments } = data;
        const { filteredActions, sortedCompleteRequiredActions } =
          requiredActions;

        if (files.length > 0) {
          const endpointData = {
            endpoint: endpoints.zoho.getPortalToken,
            isZohoRequest: true
          };

          try {
            const response = await safePostCall(
              endpoints.awsProxy.safePost,
              endpointData,
              {
                cancelToken: cancelTokenSource.token
              }
            );

            const token = response.data.details.output;

            try {
              const fileNamesRelatedToRA = [];
              const results = await Promise.all(
                filteredActions.map(async (action, index) => {
                  if (action.type !== 'fileUpload') {
                    return Promise.resolve();
                  }

                  // if action is optional, early return if no file or no valid date is present
                  if (!action.required) {
                    if (action.expires && !expirationDates[index]) {
                      return Promise.resolve();
                    } else {
                      if (!(Array.isArray(files) && files.length > 0)) {
                        return Promise.resolve();
                      }
                    }
                  }

                  const fileInfo = files[index];

                  const date = (() => {
                    if (action.expires && !action.defaultExpirationDate) {
                      return formatDateToMMDDYYYY(expirationDates[index]);
                    }
                    if (action.expires && action.defaultExpirationDate) {
                      return action.defaultExpirationDate;
                    }
                    return null;
                  })();

                  const baseName = action.name;
                  const file = fileInfo[0];
                  const fileName = file.name;
                  const extension = fileName.split('.')?.pop()?.toLowerCase();
                  const formattedStateName = action.state
                    ? action.state.replace(/\s/g, '_')
                    : null;
                  const formattedCarriername = data.carrierName.replace(
                    /\s/g,
                    '_'
                  );

                  const formattedFileName = `${
                    action.carrierBased ? `${formattedCarriername}-` : ''
                  }${baseName}${date ? `-${date}` : ''}${
                    formattedStateName ? `-${formattedStateName}` : ''
                  }-${user.First_Name}_${user.Last_Name}.${extension}`;

                  const fileFormData = new FormData();
                  fileFormData.append('file', file);
                  fileFormData.append('token', token);
                  fileFormData.append('fileName', formattedFileName);
                  fileFormData.append(
                    'url',
                    `https://www.zohoapis.com/crm/v7/Contacts/${user.id}/Attachments`
                  );

                  return safeAWSCallPost(
                    '/appointments/file-upload',
                    fileFormData,
                    false,
                    {
                      cancelToken: cancelTokenSource.token,
                      headers: {
                        'Content-Type': 'multipart/form-data'
                      }
                    }
                  ).then(uploadResponse => {
                    if (uploadResponse === null) {
                      return Promise.reject(`Error uploading ${file.fileName}`);
                    } else {
                      // only add the file name if the upload was successful
                      fileNamesRelatedToRA.push(formattedFileName);
                    }

                    return uploadResponse;
                  });
                })
              );

              const hasErrors = results.some(result => result === null);
              if (hasErrors) {
                console.error('Some file uploads failed');
                dispatch(
                  showAlert({
                    message:
                      'Something went wrong while uploading your files. Try again later.',
                    code: 'error'
                  })
                );
                dispatch(setIsSendingRequestappointments(false));
                resolve(false);
              } else {
                const alreadyUploadedValidFiles = getAlreadyUploadedValidFiles(
                  attachments,
                  data.states,
                  sortedCompleteRequiredActions,
                  data.carrierName
                );

                alreadyUploadedValidFiles.forEach(fileName =>
                  fileNamesRelatedToRA.push(fileName)
                );

                const appointmentSuccess = await sendRequestedAppointments(
                  user,
                  data,
                  {
                    ...formData,
                    fileNames: fileNamesRelatedToRA,
                    acknowledgements: getAcknowledgements(
                      sortedCompleteRequiredActions,
                      data
                    )
                  },
                  cancelTokenSource,
                  dispatch,
                  onSuccess
                );
                dispatch(setIsSendingRequestappointments(false));
                resolve(appointmentSuccess);
              }
            } catch (uploadError) {
              console.error('Error while uploading files:', uploadError);
              dispatch(
                showAlert({
                  message: 'Something went wrong. Try again later.',
                  code: 'error'
                })
              );
              dispatch(setIsSendingRequestappointments(false));
              resolve(false);
            }
          } catch (tokenError) {
            console.error(tokenError);
            dispatch(
              showAlert({
                message: 'Something went wrong. Try again later.',
                code: 'error'
              })
            );
            dispatch(setIsSendingRequestappointments(false));
            resolve(false);
          }
        } else {
          try {
            const fileNamesRelatedToRA = getAlreadyUploadedValidFiles(
              attachments,
              data.states,
              sortedCompleteRequiredActions,
              data.carrierName
            );
            const appointmentSuccess = await sendRequestedAppointments(
              user,
              data,
              {
                ...formData,
                fileNames: fileNamesRelatedToRA,
                acknowledgements: getAcknowledgements(
                  sortedCompleteRequiredActions,
                  data
                )
              },
              cancelTokenSource,
              dispatch,
              onSuccess
            );
            dispatch(setIsSendingRequestappointments(false));
            resolve(appointmentSuccess);
          } catch (appointmentError) {
            dispatch(setIsSendingRequestappointments(false));
            resolve(false);
          }
        }
      } catch (generalError) {
        dispatch(setIsSendingRequestappointments(false));
        resolve(false);
      }
    });
  };

/**
 * sends a Neo Insurance contracting request, which has no states, transfer type or required actions
 * @param carrierId - Zoho id of the Neo Insurance carrier
 * @param questions - ordered list of { question, answer } rows answered by the agent
 */
export function createNeoContractingRequest({ carrierId, questions }) {
  return async (dispatch, getState) => {
    dispatch(setIsSendingRequestappointments(true));

    try {
      const user = getState().user.zohoData;

      dispatch(showAlert({ message: 'Sending request', code: 'info' }));

      const response = await safePostCall(endpoints.awsProxy.safePost, {
        isZohoRequest: true,
        endpoint: endpoints.zoho.createNeoContractingRequest,
        agentId: String(user.id),
        carrierId: String(carrierId),
        questions
      });

      const { ok, message } = parseZohoOkMessageOutput(
        response?.data?.details?.output
      );

      if (!ok) {
        dispatch(
          showAlert({
            message:
              message ||
              'An error occurred while creating your contracting request',
            code: 'error'
          })
        );

        return false;
      }

      // same refresh as a regular appointment request so the sidebar reflects the new request
      await dispatch(getAppointmentsViewData());
      dispatch(
        showAlert({
          message: message || 'Request sent successfully!',
          code: 'success'
        })
      );
      // lives in redux because the sidebar swaps to the pending view and unmounts the Neo form
      dispatch(setIsNeoSubmissionModalOpen(true));

      return true;
    } catch (error) {
      console.error(error);
      dispatch(
        showAlert({
          message: 'Something went wrong. Try again later.',
          code: 'error'
        })
      );

      return false;
    } finally {
      dispatch(setIsSendingRequestappointments(false));
    }
  };
}

/**
 * updates the agent status of all requested appointments for a given carrier
 * @param agentStatus - new status to set
 * @param carrier - carrier whose requested appointments should be updated
 */
export const updateSpecificAppointmentStatus =
  (agentStatus, carrier) => async (dispatch, getState) => {
    dispatch(setIsUpdatingRequestAppointments(true));
    const requestedAppointments = getState().appointments.requestedAppointments;

    const filteredRequests = requestedAppointments.filter(
      request => request.carrier === carrier
    );
    dispatch(showAlert({ message: 'Updating request status', code: 'info' }));
    filteredRequests.map(async (request, index) => {
      await safePostCall(endpoints.awsProxy.safePost, {
        appointmentRequestID: request.id,
        agentStatus,
        endpoint: endpoints.zoho.updateSpecificAppointmentStatus,
        isZohoRequest: true
      })
        .then(async () => {
          if (filteredRequests.length === index + 1) {
            await dispatch(getAppointmentsViewData());
            dispatch(
              showAlert({
                message: 'Updated successfully!',
                code: 'success'
              })
            );
            dispatch(setIsUpdatingRequestAppointments(false));
          }
        })
        .catch(() => {
          dispatch(
            showAlert({
              message: 'Something went wrong. Try again later.',
              code: 'error'
            })
          );
          dispatch(setIsUpdatingRequestAppointments(false));
        });
    });
  };
